import { NavContentLinkProps, NavSubLinkProps } from './types';
import NavSubLink from './NavSubLink';
import NavSubMenuNestedLink from './NavSubMenuNestedLink';
import { ArrowRight } from '@1debit/icons';
import NavLink from 'components/shared/NavLink/NavLink';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { richTextHasContent } from 'utils/formatters';
import { cn } from 'utils/styles';
import { generateNavId, navSubMenuEyebrowClassName, subMenuHasNestedItems } from 'components/MainNav/utils';

interface NavSubMenuProps extends NavContentLinkProps {
  enableNestedDrillIn?: boolean;
  onNestedSubLinkClick?: (subLink: NavSubLinkProps) => void;
}

const NavSubMenu = ({ link, subMenu, enableNestedDrillIn, onNestedSubLinkClick }: NavSubMenuProps) => {
  const { sys: linkSys, url, label, internalPage } = link;
  const { mainDescription, linkLabel, sublinksCollection, sys: subMenuSys, eyebrow, titleOverride } = subMenu!;
  const subLinks = sublinksCollection.items;
  const mainLinkId = generateNavId(link);
  const showNestedDrillIn = enableNestedDrillIn && subMenuHasNestedItems(subMenu);

  return (
    <div className={'pt-6 pr-6 max-sm:pb-20'}>
      {/* Header — the drill-in back bar already names the section, so it would read twice. */}
      {!showNestedDrillIn && (
        <div className="flex flex-col gap-4 pb-5.5">
          <div className="flex flex-col">
            {richTextHasContent(eyebrow?.json) && (
              <div
                className={navSubMenuEyebrowClassName}
                {...liveUpdateAttrs(subMenuSys?.id, 'eyebrow')}
                data-testid="nav-sub-menu-eyebrow">
                <RichTextCopy copy={eyebrow} ySpacing="none" inline />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p
                className="font-sans text-heading-md"
                id={`${mainLinkId}_title`}
                {...(titleOverride
                  ? liveUpdateAttrs(subMenuSys?.id, 'titleOverride')
                  : liveUpdateAttrs(linkSys?.id, 'label'))}>
                {titleOverride ?? label}
              </p>
              {mainDescription && (
                <p
                  className="font-serif text-sm"
                  id={`${mainLinkId}_blurb`}
                  {...liveUpdateAttrs(subMenuSys?.id, 'mainDescription')}>
                  {mainDescription}
                </p>
              )}
            </div>
          </div>
          <div {...liveUpdateAttrs(subMenuSys?.id, 'linkLabel')}>
            <NavLink
              id={`${mainLinkId}_learn_more`}
              level={0}
              className="font-sans text-label-saans font-semibold hover:text-current border-b-[1.5px] rounded-b-none border-transparent hover:border-current focus-visible:underline active:scale-none"
              href={url}
              cleanLinkText={linkLabel}
              internalPage={internalPage}>
              {linkLabel} <ArrowRight />
            </NavLink>
          </div>
        </div>
      )}

      {/* Links */}
      <div className={cn('pt-4 pb-6', showNestedDrillIn ? 'space-y-10' : 'space-y-8')}>
        {subLinks.map(sublink => {
          if (!sublink?.link) return null;

          if (showNestedDrillIn) {
            return (
              <NavSubMenuNestedLink
                key={sublink.link.label}
                sublink={sublink}
                idPrefix={mainLinkId}
                enableNestedDrillIn={showNestedDrillIn}
                onNestedSubLinkClick={onNestedSubLinkClick}
              />
            );
          }

          return <NavSubLink key={sublink.link.label} idPrefix={mainLinkId} {...sublink} />;
        })}
      </div>
    </div>
  );
};

export default NavSubMenu;
